import {
	GlobalNavigationItemKind,
	GlobalNavigationPermissionsModel
} from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import {
	FormattedText,
	Icon,
	IconButton,
	KeyEventArguments,
	ReorderableList,
	TextInput,
	Typography
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { GlobalNavigationItemContent } from "./GlobalNavigationItemContent";
import {
	ensureNotNil,
	isNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import styles from "./GroupGlobalNavigationListItem.module.less";
import { GlobalNavigationListItem, getItemUniqueKey } from "./GlobalNavigationListItem";
import { AppGlobalNavigationItemInfo } from "./GlobalNavigationManagement";
import { DeleteConfirmationDialogData } from "./DeleteConfirmationDialog";
import { GroupGlobalNavigationItemMenu } from "./GroupGlobalNavigationItemMenu";
import { Guid } from "client_legacy/ndaw/guid";
import { GlobalNavigationGroupAndIndex } from "./GlobalNavigationItemMenu";
import { localize } from "client_legacy/ndaw/culture";
import { Key } from "client/Misc/commonTypes";
import { SpaceKind } from "client/Api/Workspace/workspace";
import { getClientId, setClientId } from "client/Misc/clientId";
import {
	GlobalNavigationItemFormModel,
	GroupGlobalNavigationItemFormModel,
	SpaceGlobalNavigationItemFormModel
} from "../misc";
import {
	AutoFocusState,
	useAutoFocus,
	useItemMenuState
} from "client/Hooks";
import { SavedSpace } from "client/Features/Workspace/EditSpaceDialog/EditSpaceDialog";

export type GroupGlobalNavigationListItemProps = {
	index: number;
	groupHandler: IValueInputAttachable<GroupGlobalNavigationItemFormModel>;
	autoFocusState: AutoFocusState<Guid>;
	trackingId: string;
	doesMatchSearchTerm(item: GlobalNavigationItemFormModel): boolean;
	onDelete(): void;
	onUnGroupChildren?(): void;
	permissions: GlobalNavigationPermissionsModel;
	onCreateSharedSpace: () => Promise<TryResult<SavedSpace>>;
	onEditSpace: (id: number) => void;
	appItemsInfo: Map<number, AppGlobalNavigationItemInfo>;
	onRemoveApp(installedAppId: number): void;
	onOpenDeleteConfirmation(data: DeleteConfirmationDialogData): Promise<boolean>;
	onAddAppAsTab?(): void;
	groups: readonly GlobalNavigationGroupAndIndex[];
	onMoveToGroup(
		itemIndex: number,
		sourceGroupIndex: number,
		targetGroupIndex: number
	): void;
	onMoveOutOfGroup(
		itemIndex: number,
		sourceGroupIndex: number
	): void;
	appIdToScroll: Guid | null;
	onAppIdToScrollChange: (id: Guid | null) => void;
	onItemAdded: () => void;
	onChangeIsBusy: (isBusy: boolean) => void;
};

export const GroupGlobalNavigationListItem = (props: GroupGlobalNavigationListItemProps): ReactElement => {
	const {
		index,
		groupHandler,
		autoFocusState,
		trackingId,
		doesMatchSearchTerm,
		appItemsInfo,
		onDelete,
		onUnGroupChildren,
		onCreateSharedSpace,
		onEditSpace,
		permissions,
		onRemoveApp,
		onOpenDeleteConfirmation,
		onAddAppAsTab,
		groups,
		onMoveOutOfGroup,
		onMoveToGroup,
		appIdToScroll,
		onAppIdToScrollChange,
		onItemAdded,
		onChangeIsBusy
	} = props;

	if (!onUnGroupChildren || !onAddAppAsTab) {
		// NOTE. Most likely this error occurs because the current group is rendered inside of another group, which is currently not allowed.
		throw new Error("Either onUnGroupChildren or onAddAppAsTab is not defined.");
	}

	const [isEditMode, setIsEditMode] = useState<boolean>(
		!groupHandler.value.id
		&& isNilOrWhiteSpace(
			groupHandler.value
				.groupData
				.name
		)
	);

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const moreButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const nameAttachable: IValueInputAttachable<string> = groupHandler
		.for("groupData")
		.for("name");

	const nameValidationState: InputValidationResult = nameAttachable.validation
		.getValidationState();

	const isValid: boolean = nameValidationState.kind === InputValidationResultKind.Valid;

	const errorToShow: string | undefined = nameValidationState.kind === InputValidationResultKind.Invalid
		? nameValidationState.errorMessage
		: undefined;

	const [autoFocus, doAutoFocus] = useAutoFocus<Guid>(getClientId(groupHandler.value), autoFocusState);

	const enterEditModeHandler = useCallback(
		(): void => {
			setIsEditMode(true);
		},
		[]
	);

	const exitEditModeHandler = useCallback(
		(): void => {
			setIsEditMode(false);
		},
		[]
	);

	const deleteHandler = useCallback(
		async (): Promise<void> => {
			const {
				name
			} = groupHandler.value
				.groupData;

			const note: ReactElement = (
				<FormattedText
					text={localize("_NoteTheSpacesWithinTheGroupWillNotBeDeleted")}
					thingsToReplace={[
						{
							what: "{{Note}}",
							filler: (
								<Typography weight="bold">
									{localize("_Note")}
								</Typography>
							)
						}
					]}
				/>
			);

			if (!await onOpenDeleteConfirmation({
				name,
				content: note
			})) {
				return;
			}

			onUnGroupChildren();
			onDelete();
		},
		[
			groupHandler.value,
			onDelete,
			onOpenDeleteConfirmation,
			onUnGroupChildren
		]
	);

	const deleteChildHandler = useCallback(
		(index: number): void => {
			groupHandler.for("groupData")
				.for("children")
				.reducer
				.removeAt(index);
		},
		[groupHandler]
	);

	const addSharedSpaceHandler = async (): Promise<void> => {
		const result: TryResult<SavedSpace> = await onCreateSharedSpace();

		if (!result.wasSuccessful) {
			return;
		}

		const space: SavedSpace = result.result;

		if (space.kind !== SpaceKind.Shared) {
			throw new Error(`Unexpected space kind was created: '${space.kind}'`);
		}

		const item: SpaceGlobalNavigationItemFormModel = setClientId({
			id: null,
			kind: GlobalNavigationItemKind.Space,
			spaceData: {
				spaceId: space.id
			},
			spaceKind: space.kind,
			name: space.name,
			userInfo: space.userInfo
		});

		autoFocusState.changeTo(
			getClientId(item)
		);

		groupHandler.for("groupData")
			.for("children")
			.reducer
			.push(item);

		onItemAdded();
	};

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			const childrenAttachable: IValueInputAttachable<readonly GlobalNavigationItemFormModel[]> = groupHandler
				.for("groupData")
				.for("children");

			const {
				value
			} = childrenAttachable;

			const childrenCopy: GlobalNavigationItemFormModel[] = [...value];
			const item: GlobalNavigationItemFormModel = childrenCopy.splice(fromIndex, 1)[0];
			childrenCopy.splice(toIndex, 0, item);

			childrenAttachable
				.reducer
				.setValue(childrenCopy);
		},
		[groupHandler]
	);

	const keyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			switch (args.key) {
				case Key.Enter:
					nameAttachable.validation
						.triggerInputFinished();

					exitEditModeHandler();
					return true;
				default:
					return false;
			}
		},
		[
			exitEditModeHandler,
			nameAttachable.validation
		]
	);

	const switchToEditModeHandler = useCallback(
		(): void => {
			doAutoFocus();
			enterEditModeHandler();
		},
		[doAutoFocus, enterEditModeHandler]
	);

	const renderHeader = (): ReactElement => {
		return (
			<GlobalNavigationItemContent
				icon={
					<div className={styles.icon}>
						<Icon value="layer-group" />
					</div>
				}
			>
				{isEditMode || !isValid
					? (
						<div className={styles.input}>
							<TextInput
								{
									...nameAttachable.attachToTextInput()
										.withOnBlur(exitEditModeHandler)
								}
								onFocus={enterEditModeHandler}
								attachedOn="right"
								trackingId="edit-group-name"
								autoFocus={autoFocus}
								onKeyDown={keyDownHandler}
							/>
						</div>
					)
					: (
						<div
							{...setTrackingId("group-item-name")}
							className={styles.clickable}
							onClick={switchToEditModeHandler}
						>
							<Typography weight="bold">
								{
									groupHandler.value
										.groupData
										.name
								}
							</Typography>
						</div>
					)
				}
			</GlobalNavigationItemContent>
		);
	};

	return (
		<>
			<ReorderableList.Group
				headerContent={renderHeader()}
				trackingId={trackingId}
				actions={(
					<IconButton
						ref={moreButtonRef}
						onClick={() => onOpenMoreMenu()}
						icon="ellipsis"
						kind="transparent"
						trackingId="more"
						shape="round"
						isActive={menuState.isOpen && !menuState.isContextMenu}
					/>
				)}
				validationMessage={errorToShow}
				isActive={menuState.isOpen}
				mouseInteraction={{
					onContextMenu: onOpenContextMenu,
					draggableMode: isEditMode || !isValid
						? "handle"
						: "header"
				}}
			>
				<ReorderableList
					onItemMove={itemMoveHandler}
				>
					{groupHandler.for("groupData")
						.for("children")
						.map((itemToRender, itemIndex) => (
							itemToRender.when(
								doesMatchSearchTerm,
								x => (
									<GlobalNavigationListItem
										itemHandler={x}
										autoFocusState={autoFocusState}
										index={itemIndex}
										permissions={permissions}
										appItemsInfo={appItemsInfo}
										onDelete={deleteChildHandler}
										onCreateSharedSpace={onCreateSharedSpace}
										onEditSpace={onEditSpace}
										doesMatchSearchTerm={doesMatchSearchTerm}
										onRemoveApp={onRemoveApp}
										onOpenDeleteConfirmation={onOpenDeleteConfirmation}
										containingGroupIndex={index}
										groups={groups}
										onMoveToGroup={onMoveToGroup}
										onMoveOutOfGroup={onMoveOutOfGroup}
										key={getItemUniqueKey(x.value)}
										appIdToScroll={appIdToScroll}
										onAppIdToScrollChange={onAppIdToScrollChange}
										onItemAdded={onItemAdded}
										onChangeIsBusy={onChangeIsBusy}
									/>
								)
							)
						))
					}
				</ReorderableList>
			</ReorderableList.Group>
			{menuState.isOpen && (
				<GroupGlobalNavigationItemMenu
					target={ensureNotNil(moreButtonRef.current)}
					onClose={onCloseMenu}
					onDelete={deleteHandler}
					onAddAppAsSpace={onAddAppAsTab}
					onCreateShareSpace={addSharedSpaceHandler}
					menuTitle={
						groupHandler.value
							.groupData
							.name
					}
					menuState={menuState}
				/>
			)}
		</>
	);
};
GroupGlobalNavigationListItem.displayName = "GroupGlobalNavigationListItem";
